import { Alert, Box, Button, Flex, Stack, Text } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { WarningIcon } from 'icons';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormButton } from 'shared/ui/Form';
import { useDeleteDocument } from '../hooks';

export type ConfirmDeleteDocumentProps = {
  ids: number | number[];
  onClose: () => void;
  onDeleted: () => void;
};

export const ConfirmDeleteDocument: React.FC<ConfirmDeleteDocumentProps> = ({
  onClose,
  onDeleted,
  ids,
}) => {
  const { t } = useTranslation(['document-delete', 'common']);
  const { deleteDocument } = useDeleteDocument();
  const count = Array.isArray(ids) ? ids.length : 1;

  const deleteDocuments = async () => {
    await deleteDocument(ids);
    onDeleted();
  };

  return (
    <Form onSubmit={deleteDocuments}>
      <Stack spacing={24}>
        <Stack spacing={16}>
          <Alert
            color="red"
            icon={<WarningIcon size={16} />}
            title={t(`modal.delete.alert.title`, {
              ns: 'document-delete',
              count,
            })}
          >
            {t(`modal.description`, {
              ns: 'document-delete',
              count,
            })}
          </Alert>

          <Box mah="50vh" sx={{ overflow: 'auto' }}>
            <Stack spacing={4}>
              <Box>
                <Text fz="lg">
                  {t(`modal.delete.alert.description`, {
                    ns: 'document-delete',
                    count,
                  })}
                </Text>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <Flex
          pos="sticky"
          bottom={0}
          mt={16}
          pb={8}
          gap={16}
          justify="flex-end"
        >
          <Button onClick={onClose} variant="default">
            {t('cancel', { ns: 'common' })}
          </Button>
          <FormButton type="submit" color="red">
            {t('yesDelete', { ns: 'common' })}
          </FormButton>
        </Flex>
      </Stack>
    </Form>
  );
};

const MODAL_ID = 'confirm-delete-document';

const Title = () => {
  const { t } = useTranslation('document-delete');
  return <>{t('modal.delete.title')}</>;
};

export const confirmDeleteDocument = (ids: number | number[]) =>
  new Promise<boolean>(resolve => {
    const close = () => {
      resolve(false);
    };

    openModal({
      modalId: MODAL_ID,
      title: <Title />,
      onClose: close,
      children: (
        <ConfirmDeleteDocument
          ids={ids}
          onClose={() => closeModal(MODAL_ID)}
          onDeleted={() => {
            resolve(true);
            closeModal(MODAL_ID);
          }}
        />
      ),
      centered: true,
    });
  });
